import { useEffect, useMemo, useState } from "react";
import { Button, LinearProgress, Modal, Typography } from "@mui/material";
import Dragdrop from "components/Dragdrop/Dragdrop";
import {
	AttachFile,
	AttachingText,
	BrowseFiles,
	DefaultSupportedFiles,
	DragFile,
	FileDuplicateText,
	FileIncorrectTypeText,
	FileSuccessText,
	FileTooLargeText,
	FileTooSmallText,
	MaxFileSize,
	MaxFileSizeHeader,
	MaxUploadsError,
	PerImage,
	SelectThreeImagesText,
	SupportedFilesHeader
} from "constants/text";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import Close from "@mui/icons-material/Close";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import UploadFile from "@mui/icons-material/UploadFile";
import { formatBytes, isValidFile } from "utils/file";
import acceptedReplacementFileUploadTypes from "constants/acceptedReplacementFileUploadTypes";
import { Part, ReplacementCartItem, ReplacementFile } from "features/reducers/replacementOrder/cart.ts";
import {
	DragFilesHereText,
	MaxFilesAlert,
	ModalFileDetails,
	ModalFileValidation,
	ModalFileValidationIcon,
	ModalLoaderBody,
	UploadAttachmentView,
	UploadModalHeader,
	UploadModalSubheader,
	UploadModalWrapper,
	UploadStatusText
} from "components/UploadModal/UploadModal.styles";
import {
	ImageUploadModalInfoContainer,
	ImageUploadModalInfoText
} from "components/Replacements/components/SelectReplacementReasonAndProblemDropdowns/SelectReplacementReasonAndProblemDropdowns.styles.ts";

export type ErrorableFileState = {
	error: string;
	file: File;
};

export interface UploadModalProps<TItem = ReplacementCartItem> {
	open: boolean;
	handleClose: (files: ErrorableFileState[], item: TItem, part: Part | null) => void;
	item: TItem;
	part?: Part;
	maxFiles: number;
	fileUploads?: ReplacementFile[];
	dragDropAcceptedFileTypes?: string;
	supportedFileTypes?: string[];
	fileSelectionText?: string | null;
	defaultSupportedFilesText?: string;
	checkIsValidFile?: (file: File, uploadedFiles: ReplacementFile[]) => boolean;
	handleUploadErrorStates?: (
		file: File,
		maxMB: number,
		fileUploads: ReplacementFile[],
		supportedFileTypes: string[]
	) => string | undefined;
}

export const processFileUploadErrorStates = (
	file: File,
	maxMB: number,
	fileUploads: ReplacementFile[] = []
): string | undefined => {
	if (fileUploads.some((selectedFile: ReplacementFile) => selectedFile.name === file.name)) {
		return FileDuplicateText;
	}

	if (file.size > maxMB * (1024 * 1024)) {
		return FileTooLargeText;
	}

	if (!acceptedReplacementFileUploadTypes.includes(file.type)) {
		return FileIncorrectTypeText;
	}

	if (file.size === 0) {
		return FileTooSmallText;
	}

	return undefined;
};

const UploadModal = <TItem,>({
	handleClose,
	item,
	part,
	open,
	maxFiles,
	fileUploads = [],
	dragDropAcceptedFileTypes: acceptedFileTypes = "image/*, .pdf",
	supportedFileTypes = acceptedReplacementFileUploadTypes,
	fileSelectionText = SelectThreeImagesText,
	defaultSupportedFilesText = DefaultSupportedFiles,
	checkIsValidFile = isValidFile,
	handleUploadErrorStates = processFileUploadErrorStates
}: UploadModalProps<TItem>) => {
	const [files, setFiles] = useState<ErrorableFileState[]>([]);
	const [error, setError] = useState<string | undefined>();

	useEffect(() => {
		setError(undefined);
		setFiles([]);
	}, [open]);

	const updateFiles = (selectedFiles: Array<File>) => {
		const filteredSelectedFiles = selectedFiles.filter((file: File) => checkIsValidFile(file, fileUploads));
		if (filteredSelectedFiles.length > maxFiles) {
			setError(MaxUploadsError);
			return;
		}

		const erroredFiles = selectedFiles.reduce(
			(accum: any, file: File) => {
				const error = handleUploadErrorStates(file, 10, fileUploads, supportedFileTypes);

				const fileNameParts = file.name.split(".");
				const normalizedFile =
					fileNameParts.pop() === "jfif"
						? new File([file.slice(0, file.size, file.type)], [...fileNameParts, "jpeg"].join("."), {
								type: file.type
							})
						: file;

				return {
					hasErrors: [!!error, accum.hasErrors].some(Boolean),
					files: [
						...accum.files,
						{
							file: normalizedFile,
							error: error
						}
					]
				};
			},
			{ hasErrors: false, files: [] }
		);

		if (!erroredFiles.hasErrors) {
			handleClose(erroredFiles.files, item, part ?? null);
			return;
		}

		setFiles(erroredFiles.files);
	};

	const onClose = () => {
		handleClose(
			files.filter((x) => x.error === undefined),
			item,
			part ?? null
		);
	};

	const showDragDrop = useMemo(() => {
		const filteredFiles = files.filter((file: ErrorableFileState) => !file.error);
		return filteredFiles.length < maxFiles;
	}, [maxFiles, files]);

	return (
		<Modal
			open={open}
			onClose={onClose}
			data-testid="upload-file-modal"
		>
			<UploadModalWrapper>
				<UploadModalHeader>
					<h6>{AttachFile}</h6>
					<button
						onClick={onClose}
						data-testid="upload-file-modal-close"
					>
						<Close />
					</button>
				</UploadModalHeader>
				<div>
					{fileSelectionText !== null && (
						<UploadModalSubheader>{fileSelectionText ?? SelectThreeImagesText}</UploadModalSubheader>
					)}
					{showDragDrop && (
						<Dragdrop
							accept={acceptedFileTypes}
							onFileUpload={updateFiles}
						>
							<UploadFile sx={{ width: "2.5rem", height: "2.5rem", marginBottom: "1rem" }} />
							<DragFilesHereText>{DragFile}</DragFilesHereText>
							<Button
								variant="outlined"
								style={{ marginTop: 0, marginBottom: "1rem" }}
							>
								{BrowseFiles}
							</Button>
							<ImageUploadModalInfoContainer>
								<ImageUploadModalInfoText>
									{SupportedFilesHeader} <span>{defaultSupportedFilesText}</span>
								</ImageUploadModalInfoText>
								<ImageUploadModalInfoText>
									{MaxFileSizeHeader} <span>{MaxFileSize}</span> {PerImage}
								</ImageUploadModalInfoText>
							</ImageUploadModalInfoContainer>
						</Dragdrop>
					)}
					{!!error && (
						<div style={{ marginTop: 10 }}>
							<MaxFilesAlert severity="error">
								<p>{error}</p>
								<span>
									<Close
										onClick={() => setError(undefined)}
										sx={{ width: "1.25rem", height: "1.25rem" }}
									/>
								</span>
							</MaxFilesAlert>
						</div>
					)}
					{files.length > 0 && (
						<div style={{ marginTop: "1.25rem" }}>
							<Typography
								variant="subtitle1"
								style={{
									textTransform: "uppercase",
									marginBottom: 7,
									fontFamily: "canada-type-gibson",
									fontWeight: 500
								}}
							>
								{AttachingText}
							</Typography>
							<div>
								{files.map(({ file, error }) => (
									<UploadAttachmentView key={file.name}>
										<ModalFileDetails>
											<Typography
												variant="body1"
												color={error && "var(--red-700)"}
											>
												{file.name}
											</Typography>
											<Typography
												variant="body2"
												color={error ? "var(--red-700)" : "var(--text-secondary)"}
												style={{ marginTop: "2px" }}
											>
												{formatBytes(file.size)}
											</Typography>
										</ModalFileDetails>
										<ModalLoaderBody>
											<div>
												<LinearProgress
													color={error ? "error" : "primary"}
													variant="determinate"
													value={error ? 5 : 100}
												/>
											</div>
										</ModalLoaderBody>
										<ModalFileValidation>
											{error ? (
												<ErrorOutline
													component={ModalFileValidationIcon}
													style={{ color: "var(--red-700)" }}
												/>
											) : (
												<CheckCircleOutline
													component={ModalFileValidationIcon}
													style={{ color: "var(--cwg-blue-900)" }}
												/>
											)}
											<UploadStatusText
												error={error}
												data-testid="upload-file-modal-status-text"
											>
												{error || FileSuccessText}
											</UploadStatusText>
										</ModalFileValidation>
									</UploadAttachmentView>
								))}
							</div>
						</div>
					)}
				</div>
			</UploadModalWrapper>
		</Modal>
	);
};

export default UploadModal;
